import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { useToast } from '../components/providers/ToastProvider';

interface UtilityStatus {
    cronIsHealthy: boolean;
    lastHeartbeat: number;
    isMaintenanceMode: boolean;
    phpBinary: string;
    cronString: string;
}

const AdminSystemUtilities: React.FC = () => {
    const [status, setStatus] = useState<UtilityStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [terminalOutput, setTerminalOutput] = useState<string>('');
    const [isExecuting, setIsExecuting] = useState(false);
    const { showToast } = useToast();

    useEffect(() => {
        fetchStatus();
    }, []);

    const fetchStatus = async () => {
        try {
            const res = await api.get('/admin/utilities');
            setStatus(res.data);
        } catch (error) {
            console.error('Failed to fetch utility status', error);
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        showToast('Copied', 'Cron command copied to clipboard', 'success');
    };

    const runCommand = async (command: string, needsDelay: boolean = false) => {
        setIsExecuting(true);
        if (needsDelay) {
            showToast('Processing', 'This background job may take a moment. Please wait.', 'info');
        }

        try {
            const res = await api.post('/admin/utilities/run', { command });
            
            setTerminalOutput(prev => `> artisan ${command}\n${res.data.output}\n\n` + prev);
            
            if (command === 'down' && res.data.secretToken) {
                // The backend now automatically attaches the 'laravel_maintenance' cookie.
                showToast('Maintenance Enabled', 'The platform is offline. Your bypass cookie has been set.', 'success');
                fetchStatus();
                setIsExecuting(false);
                return;
            }

            showToast('Success', res.data.message || 'Command executed.', 'success');
            fetchStatus();
        } catch (error: any) {
            const errorMsg = error.response?.data?.output || error.message;
            setTerminalOutput(prev => `> artisan ${command}\n[ERROR] ${errorMsg}\n\n` + prev);
            
            if (command.includes('queue') && (error.response?.status === 504 || error.message.includes('timeout'))) {
                showToast('Time Limit Reached', 'Some jobs processed, but time ran out. Click again to continue.', 'warning');
            } else {
                showToast('Error', 'Failed to execute command.', 'error');
            }
        } finally {
            setIsExecuting(false);
        }
    };

    const confirmAction = (command: string, warningText: string, needsDelay: boolean = false) => {
        if (window.confirm(`${warningText}\n\nDo you want to proceed?`)) {
            runCommand(command, needsDelay);
        }
    };

    if (loading) return <div className="p-8 text-center text-on-surface-variant"><span className="animate-spin inline-block w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></span></div>;

    return (
        <div className="p-6 max-w-7xl mx-auto space-y-6 relative">
            {isExecuting && (
                <div className="absolute inset-0 bg-surface/50 backdrop-blur-sm z-50 flex items-center justify-center rounded-xl">
                    <div className="flex flex-col items-center gap-3 bg-surface p-6 rounded-2xl shadow-2xl border border-outline-variant/50">
                        <span className="animate-spin inline-block w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></span>
                        <p className="font-bold text-lg text-on-surface">Executing Command...</p>
                    </div>
                </div>
            )}

            <h1 className="text-3xl font-bold text-on-surface flex items-center gap-3">
                <span className="material-symbols-outlined text-4xl text-primary">build_circle</span>
                System Utilities
            </h1>

            {/* Dead Man's Switch Banner */}
            <div className={`p-4 rounded-xl shadow-md border ${status?.cronIsHealthy ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-red-50 border-red-200 text-red-900'}`}>
                <div className="flex gap-4">
                    <span className="material-symbols-outlined text-2xl shrink-0 mt-0.5">
                        {status?.cronIsHealthy ? 'check_circle' : 'warning'}
                    </span>
                    <div>
                        <h3 className="font-bold">Cron Heartbeat Status</h3>
                        <div className="text-sm mt-1">
                            {status?.cronIsHealthy 
                                ? "Background automation is healthy and running normally." 
                                : "CRITICAL: The background cron job has stopped. Scheduled tasks and dashboard caches are not updating."}
                        </div>
                    </div>
                </div>
            </div>

            {/* Zero-SSH Cron Generator */}
            <div className="bg-surface-container-low rounded-xl shadow-md border border-outline-variant/50 overflow-hidden">
                <div className="p-6">
                    <h2 className="text-lg font-bold text-on-surface flex items-center gap-2 mb-2">
                        <span className="material-symbols-outlined text-primary">terminal</span>
                        Zero-SSH Cron Setup
                    </h2>
                    <p className="text-sm text-on-surface-variant mb-4">
                        Copy and paste this exact line into your cPanel Cron Jobs section (Set to run Once an Hour, or Once a Day at Midnight):
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2 mt-2">
                        <code className="bg-surface-container-high text-on-surface p-3 rounded-lg flex-1 overflow-x-auto text-sm border border-outline-variant">
                            {status?.cronString}
                        </code>
                        <button 
                            className="bg-primary hover:bg-primary-container text-on-primary px-6 py-2 rounded-lg font-medium transition-colors whitespace-nowrap"
                            onClick={() => copyToClipboard(status?.cronString || '')}
                        >
                            Copy
                        </button>
                    </div>
                    <p className="text-xs text-amber-600 mt-3 italic font-medium">
                        Troubleshooting Note: If this dynamic command fails to execute, your server might be masking the true CLI path. Simply replace <code className="bg-amber-100 px-1 rounded">{status?.phpBinary}</code> with <code className="bg-amber-100 px-1 rounded">/usr/local/bin/php</code>.
                    </p>
                </div>
            </div>

            {/* Action Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                
                {/* Caching & Core */}
                <div className="bg-surface-container-low rounded-xl shadow-md border border-outline-variant/50 overflow-hidden">
                    <div className="p-6">
                        <h2 className="text-lg font-bold text-on-surface mb-1">Core Maintenance</h2>
                        <p className="text-sm text-on-surface-variant mb-6">Run these after a new codebase deployment or to fix missing images.</p>
                        
                        <div className="space-y-3">
                            <button 
                                onClick={() => runCommand('optimize:clear')} 
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-outline hover:border-primary hover:bg-primary/5 text-on-surface rounded-lg font-medium transition-all disabled:opacity-50"
                                disabled={isExecuting}
                            >
                                <span className="material-symbols-outlined">refresh</span>
                                Optimize & Clear Cache
                            </button>
                            <button 
                                onClick={() => runCommand('storage:link')} 
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-outline hover:border-primary hover:bg-primary/5 text-on-surface rounded-lg font-medium transition-all disabled:opacity-50"
                                disabled={isExecuting}
                            >
                                <span className="material-symbols-outlined">link</span>
                                Link Storage (Fix Images)
                            </button>
                        </div>
                    </div>
                </div>

                {/* Maintenance Mode */}
                <div className="bg-surface-container-low rounded-xl shadow-md border border-outline-variant/50 overflow-hidden">
                    <div className="p-6">
                        <h2 className="text-lg font-bold text-on-surface mb-1">Maintenance Mode</h2>
                        <p className="text-sm text-on-surface-variant mb-6">
                            Take the student portal offline. As an admin, you will receive a secret bypass cookie to remain online.
                        </p>
                        
                        <div className="space-y-3">
                            {status?.isMaintenanceMode ? (
                                <button 
                                    onClick={() => confirmAction('up', 'This will put the platform back online for all students.')} 
                                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-lg font-bold transition-colors disabled:opacity-50"
                                    disabled={isExecuting}
                                >
                                    Disable Maintenance Mode (Go Live)
                                </button>
                            ) : (
                                <button 
                                    onClick={() => confirmAction('down', 'This will put the platform offline for students. You will remain logged in.')} 
                                    className="w-full bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-bold transition-colors disabled:opacity-50"
                                    disabled={isExecuting}
                                >
                                    Enable Maintenance Mode
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Queue Management */}
                <div className="bg-surface-container-low rounded-xl shadow-md border border-outline-variant/50 overflow-hidden">
                    <div className="p-6">
                        <h2 className="text-lg font-bold text-on-surface mb-1">Background Queue Management</h2>
                        <p className="text-sm text-on-surface-variant mb-6">Safely manage stuck emails and background tasks without crashing memory.</p>
                        
                        <div className="space-y-3">
                            <button 
                                onClick={() => confirmAction('queue:work --stop-when-empty', 'This will safely process pending jobs and stop. Do not refresh during execution.', true)} 
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-outline hover:border-primary hover:bg-primary/5 text-on-surface rounded-lg font-medium transition-all disabled:opacity-50"
                                disabled={isExecuting}
                            >
                                <span className="material-symbols-outlined">play_circle</span>
                                Process Queue (Run Once)
                            </button>
                            <button 
                                onClick={() => runCommand('queue:retry all')} 
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-outline hover:border-primary hover:bg-primary/5 text-on-surface rounded-lg font-medium transition-all disabled:opacity-50"
                                disabled={isExecuting}
                            >
                                <span className="material-symbols-outlined">replay</span>
                                Retry Failed Jobs
                            </button>
                            <button 
                                onClick={() => confirmAction('queue:flush', 'This will permanently delete all failed jobs. Are you sure?')} 
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-500 rounded-lg font-medium transition-all disabled:opacity-50"
                                disabled={isExecuting}
                            >
                                <span className="material-symbols-outlined">delete_forever</span>
                                Flush (Delete) Failed Jobs
                            </button>
                        </div>
                    </div>
                </div>

                {/* Data Sync */}
                <div className="bg-surface-container-low rounded-xl shadow-md border border-outline-variant/50 overflow-hidden">
                    <div className="p-6">
                        <h2 className="text-lg font-bold text-on-surface mb-1">Data Sync</h2>
                        <p className="text-sm text-on-surface-variant mb-6">Force recalculations without waiting for cron.</p>
                        
                        <div className="space-y-3">
                            <button 
                                onClick={() => runCommand('dashboard:refresh-cache')} 
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary hover:bg-primary-container text-on-primary rounded-lg font-bold transition-all disabled:opacity-50"
                                disabled={isExecuting}
                            >
                                <span className="material-symbols-outlined">sync</span>
                                Force Refresh Dashboard Caches
                            </button>
                        </div>
                    </div>
                </div>

            </div>

            {/* Terminal Output */}
            <div className="bg-[#0f172a] text-slate-300 rounded-xl shadow-xl mt-6 border border-slate-700 overflow-hidden">
                <div className="p-4 border-b border-slate-700 bg-slate-900 flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-red-500"></span>
                    <span className="w-3 h-3 rounded-full bg-amber-500"></span>
                    <span className="w-3 h-3 rounded-full bg-emerald-500"></span>
                    <h2 className="text-xs font-mono uppercase tracking-widest ml-2 text-slate-400">Terminal Output</h2>
                </div>
                <div className="p-6">
                    <pre className="overflow-x-auto min-h-[150px] max-h-[400px] text-sm font-mono whitespace-pre-wrap">
                        {terminalOutput || "No commands executed yet."}
                    </pre>
                </div>
            </div>

        </div>
    );
};

export default AdminSystemUtilities;
